import { Constants } from '../../../models/constants';

type LocaleNumberInput = string | number | undefined | null;

export function getDecimalSeparator(node: string): '.' | ',' {
  return node === Constants.TENANT_ID_DO ? '.' : ',';
}

export function hasIncompleteDecimalInput(
  value: LocaleNumberInput,
  node: string,
): boolean {
  const input = value?.toString().trim() ?? '';
  if (!input) {
    return false;
  }

  const lastChar = input[input.length - 1];
  const decimalSeparator = getDecimalSeparator(node);
  const alternateSeparator = decimalSeparator === ',' ? '.' : ',';

  return lastChar === decimalSeparator || lastChar === alternateSeparator;
}

export function cleanLocaleNumberInput(
  value: LocaleNumberInput,
  node: string,
): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  let input = value?.toString().trim() ?? '';
  const decimalSeparator = getDecimalSeparator(node);
  input = normalizeLocaleInputString(input, decimalSeparator);

  if (node === Constants.TENANT_ID_DO) {
    const data = input.replaceAll(',', '') ?? '0';
    const parsed = Number(data);
    return isNaN(parsed) ? 0 : parsed;
  }

  const data = input.replaceAll('.', '').replaceAll(',', '.') ?? '0';
  const parsed = Number(data);
  return isNaN(parsed) ? 0 : parsed;
}

export function handleDecimalNumberKeydown(
  e: KeyboardEvent,
  node: string,
): void {
  const inputElement = e.target as HTMLInputElement;
  const value = inputElement.value;
  const isDominicanRepublic = node === Constants.TENANT_ID_DO;

  if (isDominicanRepublic) {
    if (e.key === ',' && !value.includes('.')) {
      e.preventDefault();

      const selectionStart = inputElement.selectionStart ?? 0;
      const selectionEnd = inputElement.selectionEnd ?? 0;
      const newValue =
        value.slice(0, selectionStart) + '.' + value.slice(selectionEnd);

      inputElement.value = newValue;
      inputElement.setSelectionRange(
        selectionStart + 1,
        selectionStart + 1,
      );

      const event = new Event('input', { bubbles: true });
      inputElement.dispatchEvent(event);
      return;
    }
  } else if (e.key === '.' && !value.includes(',')) {
    e.preventDefault();

    const selectionStart = inputElement.selectionStart ?? 0;
    const selectionEnd = inputElement.selectionEnd ?? 0;
    const newValue =
      value.slice(0, selectionStart) + ',' + value.slice(selectionEnd);

    inputElement.value = newValue;
    inputElement.setSelectionRange(selectionStart + 1, selectionStart + 1);

    const event = new Event('input', { bubbles: true });
    inputElement.dispatchEvent(event);
    return;
  }

  const isCtrlOrCmdShortcut =
    (e.ctrlKey || e.metaKey) &&
    ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase());

  if (isCtrlOrCmdShortcut) {
    return;
  }

  const decimalSep = getDecimalSeparator(node);
  const decimalKeyOk = isDominicanRepublic
    ? e.key === '.' && !value.includes('.')
    : e.key === ',' && !value.includes(',');

  if (
    ['Tab', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(
      e.key,
    ) ||
    decimalKeyOk
  ) {
    return;
  }

  if (!isNaN(Number(e.key)) && e.key !== ' ') {
    const decimalIndex = value.indexOf(decimalSep);
    const hasSelection =
      (inputElement.selectionStart ?? 0) !==
      (inputElement.selectionEnd ?? 0);
    const cursorPos = inputElement.selectionStart ?? value.length;
    const [, decimalPart] = value.split(decimalSep);

    const isTypingInDecimals =
      decimalIndex !== -1 && cursorPos > decimalIndex && !hasSelection;

    if (isTypingInDecimals && decimalPart && decimalPart.length >= 2) {
      e.preventDefault();
    }
    return;
  }

  e.preventDefault();
}

export function handleDecimalNumberPaste(
  e: ClipboardEvent,
  node: string,
): void {
  e.preventDefault();

  const inputElement = e.target as HTMLInputElement;
  const pasted = e.clipboardData?.getData('text') ?? '';
  const value = inputElement.value;
  const selectionStart = inputElement.selectionStart ?? 0;
  const selectionEnd = inputElement.selectionEnd ?? 0;
  const merged =
    value.slice(0, selectionStart) + pasted + value.slice(selectionEnd);
  const sanitized = sanitizeDecimalPasteValue(merged, node);

  inputElement.value = sanitized;
  inputElement.setSelectionRange(sanitized.length, sanitized.length);
  inputElement.dispatchEvent(new Event('input', { bubbles: true }));
}

function sanitizeDecimalPasteValue(input: string, node: string): string {
  const decimalSep = getDecimalSeparator(node);
  const otherSep = decimalSep === ',' ? '.' : ',';
  const sanitized = input.replace(/[^\d.,]/g, '');

  const lastDecimalSep = sanitized.lastIndexOf(decimalSep);
  const lastOtherSep = sanitized.lastIndexOf(otherSep);
  const lastSeparator = Math.max(lastDecimalSep, lastOtherSep);

  // Same rule as normalizeLocaleInputString: the non-decimal separator
  // followed by more than two digits is thousands grouping (1.234 → 1234),
  // unless the local decimal separator is also present (1,234.567).
  const IS_THOUSANDS_GROUPING =
    lastSeparator === lastOtherSep &&
    lastDecimalSep === -1 &&
    sanitized.slice(lastSeparator + 1).length > 2;

  if (lastSeparator === -1 || IS_THOUSANDS_GROUPING) {
    return sanitized.replace(/[.,]/g, '');
  }

  const decimalPart = sanitized
    .slice(lastSeparator + 1)
    .replace(/[.,]/g, '')
    .slice(0, 2);
  const integerPart = sanitized.slice(0, lastSeparator).replace(/[.,]/g, '');

  if (!decimalPart.length) {
    return `${integerPart}${decimalSep}`;
  }

  return `${integerPart}${decimalSep}${decimalPart}`;
}

function normalizeLocaleInputString(
  input: string,
  decimalSeparator: '.' | ',',
): string {
  const lastDecimalSep = input.lastIndexOf(decimalSeparator);
  const otherSeparator = decimalSeparator === ',' ? '.' : ',';
  const lastOtherSep = input.lastIndexOf(otherSeparator);
  const lastSeparator = Math.max(lastDecimalSep, lastOtherSep);

  if (lastSeparator === -1) {
    return input;
  }

  let decimalPart = input.slice(lastSeparator + 1);
  const isDecimalSeparator = lastSeparator === lastDecimalSep;
  const isLikelyDecimal = decimalPart.length > 0 && decimalPart.length <= 2;

  if (isDecimalSeparator && decimalPart.length > 0) {
    decimalPart = decimalPart.slice(0, 2);
    const integerPart = input.slice(0, lastSeparator).replace(/[.,]/g, '');
    return integerPart + decimalSeparator + decimalPart;
  }

  if (isLikelyDecimal) {
    const integerPart = input.slice(0, lastSeparator).replace(/[.,]/g, '');
    return integerPart + decimalSeparator + decimalPart;
  }

  return input.replace(/[.,]/g, '');
}
